import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useReceiveStock } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { RECEIPT_REFERENCE_TYPE_OPTIONS } from '../../model/options';
import type { StockReceiptReferenceType } from '../../model/types';
import { LocationSelect } from './LocationSelect';

export interface ReceiveStockDialogProps {
  open: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toPositiveNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) || n <= 0 ? undefined : n;
}

/** Records a goods receipt from a supplier at a fulfilment location (page-level action). */
export function ReceiveStockDialog({ open, onClose, onResult }: ReceiveStockDialogProps) {
  const { t } = useTranslation('inventory');
  const receive = useReceiveStock();
  const { locations } = useLocationLookup();

  const [productId, setProductId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [referenceType, setReferenceType] = useState<StockReceiptReferenceType>('MANUAL');
  const [referenceId, setReferenceId] = useState('');

  useEffect(() => {
    if (open) {
      setProductId('');
      setLocationId('');
      setQuantity('');
      setReferenceType('MANUAL');
      setReferenceId('');
    }
  }, [open]);

  const quantityValue = toPositiveNumberOrUndefined(quantity);
  const valid = productId.trim() !== '' && locationId !== '' && quantityValue !== undefined;

  const submit = () => {
    if (!valid || quantityValue === undefined) return;
    receive.mutate(
      {
        productId: productId.trim(),
        locationId,
        quantity: quantityValue,
        referenceType,
        referenceId:
          referenceType === 'PURCHASE_ORDER' ? referenceId.trim() || undefined : undefined,
      },
      {
        onSuccess: () => {
          onResult('success', t('receiveStockDialog.success'));
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('receiveStockDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('receiveStockDialog.productIdLabel')}
            required
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            error={productId.trim() === ''}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <LocationSelect
            label={t('receiveStockDialog.locationLabel')}
            value={locationId}
            onChange={setLocationId}
            locations={locations}
            required
            error={locationId === ''}
            fullWidth
          />
          <TextField
            label={t('receiveStockDialog.quantityLabel')}
            type="number"
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            error={quantity.trim() !== '' && quantityValue === undefined}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
          />
          <TextField
            select
            label={t('receiveStockDialog.referenceTypeLabel')}
            value={referenceType}
            onChange={(e) => setReferenceType(e.target.value as StockReceiptReferenceType)}
            fullWidth
          >
            {RECEIPT_REFERENCE_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>
          {referenceType === 'PURCHASE_ORDER' ? (
            <TextField
              label={t('receiveStockDialog.referenceIdLabel')}
              helperText={t('receiveStockDialog.referenceIdHelper')}
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              fullWidth
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={receive.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || receive.isPending}
          startIcon={receive.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
